import { useEffect, useRef } from "react"

let nextKey = 0

// Il tasto indietro del telefono (o lo swipe dal bordo) chiude quello che è
// aperto sopra la pagina invece di cambiare pagina: aprendo si mette una
// voce nella cronologia, con lo stesso indirizzo, e tornando indietro si
// chiude. È quello che fa anche il riordino del profilo, e lo usa ogni
// foglio dal basso (SModal.js).
//
// Le cose aperte una sopra l'altra (l'esercizio a pagina intera, sopra il
// foglio per modificarlo, sopra ancora l'editor dello schema) hanno ognuna
// la sua voce, con un numero che cresce: indietro chiude solo quella in
// cima, e chi sta sotto si riconosce perché è tornato sulla sua voce o su
// una più in alto. Ogni voce sa anche quante ce ne sono fino a lei
// (`overlays`), così chi si chiude da sé (la ✕, "Fatto") toglie la sua e
// quelle sopra, se no il primo indietro dopo non farebbe niente.

// I "torna indietro" chiesti da qui, in fila: la cronologia si muove in
// differita, e chi guardasse la voce in cima mentre uno è in corso la
// troverebbe com'era prima. Chi apre aspetta, se no il "torna indietro" di
// chi si è appena chiuso toglierebbe la voce appena messa.
let queue = Promise.resolve()
let queued = 0

const top = () => window.history.state ?? {}

const whenSettled = fn => (queued ? queue.then(fn) : fn())

const push = key => {
	const overlays = (top().overlays ?? 0) + 1
	window.history.pushState({ ...window.history.state, backClose: key, overlays }, "")
	return overlays
}

// quante voci togliere si guarda quando tocca a lui, non quando lo si chiede
const goBack = howMany => {
	queued += 1
	queue = queue.then(
		() =>
			new Promise(resolve => {
				let timer
				const done = () => {
					window.removeEventListener("popstate", done)
					clearTimeout(timer)
					queued -= 1
					resolve()
				}
				const count = howMany()
				if (count <= 0) return done()
				window.addEventListener("popstate", done)
				// se il browser non si muove, la fila non resta ferma per sempre
				timer = setTimeout(done, 1000)
				window.history.go(-count)
			}),
	)
	return queue
}

// le voci dalla `keep`-esima di chi le ha messe in su, se sono ancora lì:
// se si è andati su un'altra pagina la voce in cima è quella nuova, e resta
const above = (owner, keep = 0) => () => {
	const { backClose, overlays = 0 } = top()
	return backClose >= owner.key ? overlays - (owner.level + keep) + 1 : 0
}

export function useBackClose(onBack) {
	const onBackRef = useRef(onBack)
	onBackRef.current = onBack

	useEffect(() => {
		const owner = { key: ++nextKey, level: 0 }
		let popped = false
		let closed = false
		const onPop = () => {
			// tornati su questa voce, o su una sopra: non tocca a lei
			if (top().backClose >= owner.key) return
			popped = true
			onBackRef.current()
		}
		whenSettled(() => {
			if (closed) return
			owner.level = push(owner.key)
			window.addEventListener("popstate", onPop)
		})
		return () => {
			closed = true
			window.removeEventListener("popstate", onPop)
			if (!popped && owner.level) goBack(above(owner))
		}
	}, [])
}

// Un modulo a passi (la registrazione, il circolo nuovo): il tasto indietro
// fa quello del bottone Indietro. Ogni passo avanti mette una voce, e
// tornando indietro si torna al passo della voce su cui si arriva.
//
// Le voci si mettono solo andando avanti, cioè toccando: Chrome, col tasto
// indietro, salta le voci che una pagina ha messo senza essere toccata, e
// una voce rimessa subito dopo un indietro per "tenere fermi" non reggerebbe.
// Al passo 0 indietro fa quello che farebbe senza il modulo.
export function useBackSteps(depth, goTo) {
	const goToRef = useRef(goTo)
	goToRef.current = goTo
	const depthRef = useRef(depth)
	depthRef.current = depth
	const ownerRef = useRef(null)

	useEffect(() => {
		const owner = { key: ++nextKey, level: 0, count: 0 }
		ownerRef.current = owner
		const onPop = () => {
			const { backClose, overlays = 0 } = top()
			if (!owner.count || backClose > owner.key) return
			const landed = backClose === owner.key ? overlays - owner.level + 1 : 0
			if (landed >= owner.count) return
			owner.count = landed
			goToRef.current(landed)
		}
		window.addEventListener("popstate", onPop)
		return () => {
			window.removeEventListener("popstate", onPop)
			ownerRef.current = null
			if (owner.count) goBack(above(owner))
		}
	}, [])

	useEffect(() => {
		const owner = ownerRef.current
		if (!owner) return
		if (depth < owner.count) {
			// indietro col bottone: si tolgono le voci dei passi lasciati
			owner.count = depth
			goBack(above(owner, depth))
			return
		}
		whenSettled(() => {
			if (ownerRef.current !== owner) return
			while (owner.count < depthRef.current) {
				const level = push(owner.key)
				if (!owner.count) owner.level = level
				owner.count += 1
			}
		})
	}, [depth])
}

// Prima di cambiare pagina da dentro qualcosa di aperto (l'accesso dal
// foglio, la registrazione finita, l'uscita): le sue voci si tolgono, se no
// resterebbero sotto la pagina nuova e indietro ci ripasserebbe.
export async function leaveOverlays() {
	await queue
	const count = top().overlays ?? 0
	if (count) await goBack(() => count)
}
